import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { addSOWCompletedTasks, fetchSOWCompletedTasks } from '../services/api';
import { parseTasksFromSOW } from '../services/taskParser';
import { getCurrentLocation } from '../services/geoLocation';
import { queryParamsService, getUrlCacheKey } from '../services/queryParams';
import { CONSTANTS } from '../constants/constants';
import { isValueEmpty, getDeviceTimeZone, isEndAfterStartByMinute, hasTotalTime } from '../services/utils';
import { useDialogStore, useSOWListDataStore, useVeilStore, useSOWDataStore, useAppStore } from '../store';
import { createTicketFollower } from './ticketScopedPersistence';
import { createTicketTasksStorage, mergeInMemoryTasks, TICKET_TASKS_STORAGE_PREFIX } from './ticketTasksStorage';
import { summarizeTasksStatus } from '../services/tasksStatus';
import { isCheckingPartsUsage, isPartsUsageUnverified, markPartsUsageVerified, runPartsUsageCheck, waitForMatchingSOWData } from './partsUsageCheck';
import {
  backfillPartsUsageTask,
  canBackfillPartsUsage,
  hasPartsUsageData,
  isPartsUsageTask,
  isPartsUsageComplete,
  applyPartAnswer,
  applyNotApplicable,
  withDerivedCompletion,
  syncPartsUsageTask,
} from '../services/partsUsage';
import { getPictureData, setPictureData, getFileData } from '../services/binaryDataService';


// How long a ticket's server-side submission state is trusted before it is checked
// again when the ticket's tabs are reopened.
const SUBMITTED_CHECK_INTERVAL_MS = 30000;
const MAX_TRACKED_TICKETS = 50;
const lastSubmittedCheckAt = new Map();

// Remembers when a ticket was last checked, forgetting the oldest ones beyond the cap so
// a long-lived tab that hops between many tickets does not grow this map without bound.
const recordSubmittedCheck = (sowKey, now) => {
  lastSubmittedCheckAt.delete(sowKey); // re-insert so the most recently checked ticket is newest
  lastSubmittedCheckAt.set(sowKey, now);
  if (lastSubmittedCheckAt.size > MAX_TRACKED_TICKETS) {
    lastSubmittedCheckAt.delete(lastSubmittedCheckAt.keys().next().value);
  }
};

// A built-in task keeps its key when the parts usage backfill renumbers it, so a request that is
// still pending (a GPS fix, a delayed lookup) has to follow the key. Tasks without a key come from
// the SOW, ahead of the insertion point, and keep their id.
const findTaskIdentity = (tasks, taskId) => tasks.find((task) => task.id === taskId)?.key;
const isSameTask = (task, taskId, key) => (key ? task.key === key : task.id === taskId);

// HomeTab switches tickets with history.pushState and no reload, so when the URL's ticket
// changes the store has to be rehydrated from the new ticket's record.
const ticketFollower = createTicketFollower();

export const useSOWTasksStore = create(persist((set, get) => ({
  // State
  jobsTasks: {},
  searchTerm: '',
  sortField: 'order',
  sortDirection: 'asc',
  loading: false,
  loadingTime: 0,
  error: null,
  timestamp: null,
  
  // Actions
  setSearchTerm: (searchTerm) => set({ searchTerm }),
  setSortField: (sortField) => set({ sortField }),
  setSortDirection: (sortDirection) => set({ sortDirection }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  // Helper function to wait for persistence
  persistState: async () => {
    set({ timestamp: new Date() });
    try {
      await get().persist?.rehydrate();
    } catch (error) {
      console.error('Failed to persist state:', error);
    }
  },
  
  removeBinaryDataFromTasks: async (tasks) => {
    for (let task of tasks) {
      if (task.taskData && task.taskData.measures) {
        for (let measure of task.taskData.measures) {
          if (measure.name === 'Picture' && measure.value && Array.isArray(measure.value)) {
            for (let picture of measure.value) {
              if (picture.hasOwnProperty('data')) {
                setPictureData(picture, picture.data);
                delete picture.data;
              }
            }
          }
        }
      }
    }
    
    return tasks;
  },

  fetchTasks: async (t) => {
    let sowKey = queryParamsService.getCacheKey();
    let issueKey = queryParamsService.getIssueKey();
    const { alert } = useDialogStore.getState();
    let { jobsTasks, removeBinaryDataFromTasks } = get();
    
    let tasks = jobsTasks[sowKey] || [];

    if (!isValueEmpty(tasks)) {
        // Started first so submit is held back from the very start of the check
        const backfill = get().backfillPartsUsageTask(sowKey, t).catch((error) => {
          console.error('Failed to check the parts usage task:', error);
        });
        await get().refreshSubmittedState(sowKey);
        await backfill;
        return;
    }

    // only allow 5 seconds for the loading to complete
    if (get().loadingTime + 5000 <= Date.now()) {
      set({ loading: false, loadingTime: 0 });
    }
    
    if (get().loading) {
      return;
    }
    
    set({ loading: true, error: null, loadingTime: Date.now() });

    // The technician can switch tickets in-app while this waits on the server. An obsolete
    // invocation must commit nothing: it would replace the new ticket's state, or parse the
    // new ticket's SOW under this ticket's key.
    const isCurrentTicket = () => queryParamsService.getCacheKey() === sowKey;
    // A slow response can land after the technician left and came back (A -> B -> A) and
    // already worked on tasks loaded by a later fetch; those edits must win.
    const commit = (loadedTasks) => set((state) => ({
      jobsTasks: state.jobsTasks[sowKey]?.length
        ? state.jobsTasks
        : { ...state.jobsTasks, [sowKey]: loadedTasks },
      loading: false,
      loadingTime: 0
    }));

    try {
      const apiTasks = await fetchSOWCompletedTasks();
      if (!isCurrentTicket()) return;

      if (apiTasks && apiTasks.length > 0) {
        const tasksWithoutBinaryData = await removeBinaryDataFromTasks(apiTasks);
        if (isCurrentTicket()) commit(tasksWithoutBinaryData);
        return;
      }

      const { sowData, fetchSOWData } = useSOWDataStore.getState();

      if (!sowData || sowData.workorderKey !== issueKey) {
        await fetchSOWData();
        if (!isCurrentTicket()) return;
        const updatedSOWData = useSOWDataStore.getState().sowData;
        commit(updatedSOWData?.scopeOfWork?.sow ? parseTasksFromSOW(updatedSOWData.scopeOfWork.sow, t, updatedSOWData.scopeOfWork.components) : []);
      } else {
        // A refresh may be adding parts: parse what it brings, not the cached copy it is replacing.
        // If it outlasts the wait, the cached copy still gives the technician their tasks (an empty
        // list would stay empty until the tab is reopened) and the check below catches up with the
        // refresh, holding Submit back until it has.
        const isRefreshing = useSOWDataStore.getState().sowLoading;
        const refreshed = isRefreshing ? await waitForMatchingSOWData() : sowData;
        if (!isCurrentTicket()) return;
        const current = refreshed ?? sowData;
        commit(current?.scopeOfWork?.sow ? parseTasksFromSOW(current.scopeOfWork.sow, t, current.scopeOfWork.components) : []);
        if (!refreshed) {
          get().backfillPartsUsageTask(sowKey, t).catch((error) => {
            console.error('Failed to check the parts usage task:', error);
          });
        }
      }

    } catch (error) {
      if (!isCurrentTicket()) return;
      set({ error: error.message, loading: false, loadingTime: 0 });
      if (t) {
        alert(t('errorLoadingTasks', { error: error.message }));
      } else {
        alert(`Error loading tasks: ${error.message}`);
      }
    }
  },
  
  // Tickets stored before the parts usage task existed are never re-parsed, so add it to them
  // once the work order's parts are known, fetching them (or waiting for a fetch already
  // running) if need be. Submit is held back while this runs. Only the ticket it was asked
  // about is touched.
  backfillPartsUsageTask: async (sowKey, t) => {
    if (queryParamsService.getCacheKey() !== sowKey) return;
    const needsCheck = (tasks) => canBackfillPartsUsage(tasks) || tasks.some((task) => isPartsUsageTask(task) && !task.submitted);
    if (!needsCheck(get().jobsTasks[sowKey] || [])) {
      // Nothing left to verify, so an earlier failed check must not keep holding Submit back
      markPartsUsageVerified(sowKey, true);
      return;
    }

    await runPartsUsageCheck(sowKey, async () => {
      const sowData = await waitForMatchingSOWData();
      if (queryParamsService.getCacheKey() !== sowKey) return;
      markPartsUsageVerified(sowKey, Boolean(sowData));
      if (!sowData) return;

      const current = get().jobsTasks[sowKey] || [];
      const components = sowData.scopeOfWork?.components;
      const backfilled = syncPartsUsageTask(backfillPartsUsageTask(current, components, t), components);
      if (backfilled !== current) {
        set((state) => ({ jobsTasks: { ...state.jobsTasks, [sowKey]: backfilled } }));
      }
    });
  },

  // Applies `update` to the parts usage task's data, unless it is already submitted. Its
  // completion follows from the answers, there is no timer to stop.
  updatePartsUsage: (taskId, update) => {
    const sowKey = queryParamsService.getCacheKey();
    const tasks = get().jobsTasks[sowKey] || [];
    const isEditable = (task) => task.id === taskId && isPartsUsageTask(task) && hasPartsUsageData(task) && !task.submitted;
    if (!tasks.some(isEditable)) return;

    const updatedTasks = tasks.map((task) => {
      if (!isEditable(task)) return task;
      const edited = { ...task, taskData: { ...task.taskData, partsUsage: update(task.taskData.partsUsage) }, lastModified: Date.now() };
      return withDerivedCompletion(edited);
    });
    set((state) => ({ jobsTasks: { ...state.jobsTasks, [sowKey]: updatedTasks } }));
  },

  setPartUsageAnswer: (taskId, lineKey, answer) =>
    get().updatePartsUsage(taskId, (partsUsage) => applyPartAnswer(partsUsage, lineKey, answer)),

  setPartsUsageNotApplicable: (taskId, notApplicable) =>
    get().updatePartsUsage(taskId, (partsUsage) => applyNotApplicable(partsUsage, notApplicable)),

  // Initialize and fetch data
  initializeTasks: async (t) => {
    queryParamsService.initialize();

    await ticketFollower.follow(async ({ isRetry }) => {
      // rehydrate() leaves the state alone when the new ticket has no stored record, so
      // clear the previous ticket's tasks first. Not persisted: they are not this ticket's.
      // A retry after a failed read keeps the work done in memory meanwhile and merges it
      // with what the record holds.
      const keptInMemory = isRetry ? get().jobsTasks : null;
      if (!isRetry) set({ jobsTasks: {} });
      await useSOWTasksStore.persist.rehydrate();
      if (keptInMemory) {
        set((state) => ({ jobsTasks: mergeInMemoryTasks(state.jobsTasks, keptInMemory) }));
        return () => set({}); // persists the merged tasks once the load is confirmed
      }
      // A request still running for the previous ticket must not make the new ticket's
      // fetch bail out at the loading guard.
      set({ loading: false, loadingTime: 0, error: null });
    });

    await get().fetchTasks(t);
  },
  
  updateTimeSpent: (task, state) => {
    if (!isValueEmpty(task.startTimestamp)) {
        // A paused task's clock is frozen at the moment it was paused, so the
        // current (still open) pause interval is never counted as worked time.
        const now = task.paused && task.pauseStartTime ? task.pauseStartTime : Date.now();

        // Calculate total elapsed time since start
        const totalElapsedSeconds = Math.floor((now - task.startTimestamp) / 1000);

        // Calculate current time spent (elapsed minus total paused time)
        const currentTimeSpent = totalElapsedSeconds - (task.totalPausedTime || 0);
        if (state) {
          state.updated = true;
        }

        return {
          ...task,
          timeSpent: Math.max(0, currentTimeSpent),
          lastModified: Date.now() // Ensure non-negative
        };
    }
    return task;
  },
  
  handleTimerTick: () => {
    const { jobsTasks } = get();
    let sowKey = queryParamsService.getCacheKey();
    let tasks = jobsTasks[sowKey] || [];
    let state = {updated: false};

    const updatedTasks = tasks.map(task => {
      if (task.status !== CONSTANTS.STATUS.COMPLETED && !task.paused && task.startTimestamp) {
        return get().updateTimeSpent(task, state);
      }
      return task;
    });
    if (state.updated) {
      jobsTasks[sowKey] = updatedTasks;
      set({ jobsTasks: { ...jobsTasks } });
    }
  },
  
  startTask: async (taskId, t) => {
    const { jobsTasks, fetchLocation } = get();
    const { confirm } = useDialogStore.getState();
    let sowKey = queryParamsService.getCacheKey();
    let tasks = jobsTasks[sowKey] || [];
    const now = Date.now();

    const requestedTask = tasks.find((task) => task.id === taskId);
    // The parts usage task has no timer to start
    if (isPartsUsageTask(requestedTask)) return;

    // Skip confirmation for the very first task (id = 1)
    if (taskId !== 1) {
      // Find all tasks with lower IDs that are not completed
      const previousTasks = tasks.filter(task => 
        task.id < taskId && 
        task.id >= 1 && // Ensure we're only checking from task 1 upwards
        task.status !== CONSTANTS.STATUS.COMPLETED
      );

      // If there are incomplete previous tasks, show confirmation
      if (previousTasks.length > 0) {
        const incompleteTaskIds = previousTasks.map(task => task.id).sort((a, b) => a - b);
        const message = t('startOutOfOrderTaskconfirmationMess', { 
          currentTask: taskId, 
          incompleteTasks: incompleteTaskIds.join(', ') 
        });

        // Wrap confirm in a promise to await user decision
        const userConfirmed = await new Promise((resolve) => {
          confirm(message, () => {
            resolve(true); // User confirmed
          }, () => {
            resolve(false); // User cancelled
          });
        });

        // If user cancelled, don't start the task
        if (!userConfirmed) {
          return;
        }
      }
    }

    // The list can change while the confirmation is open (the parts usage backfill inserts a task
    // and shifts the ids after it), so start the task on the current list, found by its key.
    const currentTasks = get().jobsTasks[sowKey] || [];
    const startedTask = requestedTask?.key
      ? currentTasks.find((task) => task.key === requestedTask.key)
      : requestedTask && currentTasks.find((task) => task.id === taskId);
    if (!startedTask) return;

    const updatedTasks = currentTasks.map(task => {
      if (task.id === startedTask.id) {
        // Check if this is a fresh start or resuming from pause
        const isFreshStart = !task.startTime;

        return {
          ...task,
          status: CONSTANTS.STATUS.IN_PROGRESS,
          paused: false,
          startTime: task.startTime || new Date().toISOString(),
          // Timer tracking fields
          startTimestamp: task.startTimestamp || now, // Store as milliseconds
          lastResumeTime: now,
          totalPausedTime: task.totalPausedTime || 0,
          pauseStartTime: null,
          timeSpent: task.timeSpent || 0,
          lastModified: Date.now()
        };
      }
      return task;
    });

    set((state) => ({ jobsTasks: { ...state.jobsTasks, [sowKey]: updatedTasks } }));

    fetchLocation(startedTask.id, t);
  },

  isTaskReadyToStop: (taskId, t) => {
    const { jobsTasks } = get();
    let sowKey = queryParamsService.getCacheKey();
    let tasks = jobsTasks[sowKey] || [];
    const { alert } = useDialogStore.getState();
    
    const task = tasks.find(t => t.id === taskId);
    if (task && task.taskData && task.taskData.measures) {
      for (let i = 0; i < task.taskData.measures.length; i++) {
        if (task.taskData.measures[i].mandatory && isValueEmpty(task.taskData.measures[i].value)) {
          if (t) {
            alert(t(task.taskData.measures[i].name + 'IsMandatory'));
          } else {
            alert(`${task.taskData.measures[i].name} is mandatory`);
          }
          return false;
        }
      }
    }
    return true;
  },
  
  getTasksStatus: (sowId) => {
    if (isValueEmpty(sowId)) {
        sowId = queryParamsService.getCacheKey();
    }
    return summarizeTasksStatus(get().jobsTasks[sowId]);
  },
  
  areTasksReadyToSubmit: (t) => {
    const { jobsTasks } = get();
    let sowKey = queryParamsService.getCacheKey();
    let tasks = jobsTasks[sowKey] || [];
    const { alert } = useDialogStore.getState();
    
    for (let i = 0; i < tasks.length; i++) {
      // No timer on this task: it is ready once every part is answered (or Not applicable is checked)
      if (isPartsUsageTask(tasks[i])) {
        if (hasPartsUsageData(tasks[i]) && !isPartsUsageComplete(tasks[i].taskData.partsUsage)) {
          alert(t ? t('partsUsageIncomplete') : 'Record the usage status of every part');
          return false;
        }
        continue;
      }
      if (!tasks[i].completed) {
        if (t) {
          alert(t('taskNotCompleted', { id: tasks[i].id }));
        } else {
          alert(`Task ${tasks[i].id} is not completed`);
        }
        return false;
      }
      if (!hasTotalTime(tasks[i])) {
        // Completed tasks have their +/- time buttons disabled, so point to the controls that work.
        alert(t ? t('taskTotalTimeEmptyCompleted', { id: tasks[i].id }) : `Task ${tasks[i].id} has no total time. Adjust its start or completed date, or undo the task and complete it again`);
        return false;
      }
      const { startTime, endTime } = tasks[i];
      if (startTime && endTime && !isEndAfterStartByMinute(new Date(startTime).getTime(), new Date(endTime).getTime())) {
        alert(t ? t('taskTimesEqual', { id: tasks[i].id }) : `Task ${tasks[i].id} must have a completed date after its start date`);
        return false;
      }
    }
    return true;
  },
  
  areTasksSubmitted: (t) => {
      const { jobsTasks } = get();
      let sowKey = queryParamsService.getCacheKey();
      let tasks = jobsTasks[sowKey] || [];
      
      for (let i = 0; i < tasks.length; i++) {
        if (!tasks[i].submitted) {
          return false;
        }
      }
      return true;
    },
    
    getTasksWithBinaryData: async (tasks) => {

      const tasksWithBinaryData = JSON.parse(JSON.stringify(tasks));
      const timeZone = getDeviceTimeZone();

      // Process each task to include binary data for pictures and documents
      for (let task of tasksWithBinaryData) {
        // Tag with the device's IANA zone so Flow can render startTime/endTime
        // in the technician's local time instead of the raw UTC instant.
        task.timeZone = timeZone;

        if (task.taskData && task.taskData.measures) {
          for (let measure of task.taskData.measures) {
            // Handle picture measures - value is an array of picture objects
            if (measure.name === 'Picture' && measure.value && Array.isArray(measure.value)) {
              for (let picture of measure.value) {
                try {
                  const binaryData = await getPictureData(picture);
                  picture.data = binaryData;
                } catch (error) {
                  console.warn(`Failed to load binary data for picture ${picture.id}:`, error);
                  picture.data = null;
                }
              }
            }
            
            // Handle document measures - value is an array of file objects
            if (measure.name === 'Document' && measure.value && Array.isArray(measure.value)) {
              for (let file of measure.value) {
                try {
                  const binaryData = await getFileData(file);
                  file.data = binaryData;
                } catch (error) {
                  console.warn(`Failed to load binary data for file ${file.id}:`, error);
                  file.data = null;
                }
              }
            }
          }
        }
      }
      
      return tasksWithBinaryData;
    },
  
  // Pulls the server's record of completed tasks for this SOW and, if it shows
  // the tasks were actually recorded (backend already committed the comment,
  // attachments and custom field before a client-side exception hid the
  // response), updates local state to match. Returns true when the server
  // confirms the submission went through, false otherwise. This is what lets
  // submitTasks tell "genuinely failed" apart from "succeeded server-side but
  // the client never found out" instead of leaving the panel stuck on
  // unsubmitted forever (a retry would just hit the backend's existing-tasks
  // guard and fail again).
  reconcileSubmittedTasks: async (sowKey) => {
    // Captured before the request: the technician can switch tickets in-app while it is
    // in flight, and the answer must only ever touch the ticket it was asked about.
    // The request below snapshots the credentials of whichever ticket is open when it
    // starts, so it is only made while that is still `sowKey`.
    if (queryParamsService.getCacheKey() !== sowKey) return false;
    const issueKey = queryParamsService.getIssueKey();
    const apiTasks = await fetchSOWCompletedTasks();

    if (apiTasks && apiTasks.length > 0 && apiTasks.every(task => task.submitted)) {
      // Picture payloads belong in binary storage, not in the persisted record.
      const tasksWithoutBinaryData = await get().removeBinaryDataFromTasks(apiTasks);
      set({ jobsTasks: { ...get().jobsTasks, [sowKey]: tasksWithoutBinaryData }, loading: false });
      useSOWListDataStore.getState().updateTaskStatus(issueKey, CONSTANTS.STATUS.COMPLETED);
      return true;
    }
    return false;
  },

  // Tasks stored locally can be stale when someone else submitted this ticket in the
  // meantime. Asks the server (online only, throttled per ticket) and adopts its tasks
  // if they are all submitted. Anything else — nothing submitted yet, or a failed
  // request — leaves the technician's local work untouched.
  refreshSubmittedState: async (sowKey) => {
    const tasks = get().jobsTasks[sowKey] || [];
    if (tasks.every((task) => task.submitted)) return;
    if (!useAppStore.getState().isOnline) return;

    const now = Date.now();
    if (now - (lastSubmittedCheckAt.get(sowKey) ?? 0) < SUBMITTED_CHECK_INTERVAL_MS) return;
    recordSubmittedCheck(sowKey, now);

    try {
      await get().reconcileSubmittedTasks(sowKey);
    } catch (error) {
      console.error('Failed to refresh submitted state:', error);
    }
  },

  submitTasks: async (t) => {
    const { areTasksReadyToSubmit, jobsTasks, getTasksWithBinaryData, reconcileSubmittedTasks } = get();
    let sowKey = queryParamsService.getCacheKey();
    const issueKey = queryParamsService.getIssueKey(); // before any await — see reconcileSubmittedTasks
    let tasks = jobsTasks[sowKey] || [];
    const { showVeil, hideVeil } = useVeilStore.getState();
    const { alert } = useDialogStore.getState();

    // The parts usage task may still be on its way into this ticket's tasks
    if (isCheckingPartsUsage(sowKey)) {
      alert(t ? t('partsUsageChecking') : 'Checking whether this work order has parts to record, try again in a moment');
      return;
    }
    // A ticket whose parts could not be loaded earlier gets one more try before it may be submitted
    if (isPartsUsageUnverified(sowKey)) {
      await get().backfillPartsUsageTask(sowKey, t);
      if (isPartsUsageUnverified(sowKey)) {
        alert(t ? t('partsUsageUnverified') : 'The parts of this work order could not be loaded, try again when online');
        return;
      }
      tasks = get().jobsTasks[sowKey] || [];
    }
    if (!areTasksReadyToSubmit(t)) {
      return;
    }
    showVeil();
    try {
      const payload = await getTasksWithBinaryData(tasks);
      // addSOWCompletedTasks snapshots the open ticket's credentials when it is called, so
      // this ticket's tasks must never be sent once the technician has moved to another.
      if (queryParamsService.getCacheKey() !== sowKey) {
        hideVeil();
        return;
      }
      let response = await addSOWCompletedTasks(payload);
      if (response) {
        if (response.queued) {
          // After an in-app switch this ticket's tasks are no longer in memory; writing them
          // back would replace the new ticket's. Its server state is checked on next open.
          if (queryParamsService.getCacheKey() === sowKey) {
            // Re-read: the state may have been reloaded (A -> B -> A) while submitting.
            set((state) => ({
              jobsTasks: {
                ...state.jobsTasks,
                [sowKey]: (state.jobsTasks[sowKey] ?? tasks).map((task) => ({ ...task, submitted: true })),
              },
              loading: false,
            }));
          }

          // update home tasks list
          useSOWListDataStore.getState().updateTaskStatus(issueKey, CONSTANTS.STATUS.COMPLETED);

        } else if (queryParamsService.getCacheKey() === sowKey) {
          // Only while still on this ticket: the request would use the open ticket's credentials.
          let apiTasks = await fetchSOWCompletedTasks();

          if (apiTasks && apiTasks.length > 0 && queryParamsService.getCacheKey() === sowKey) {
            const tasksWithoutBinaryData = await get().removeBinaryDataFromTasks(apiTasks);
            if (queryParamsService.getCacheKey() === sowKey) {
              jobsTasks[sowKey] = tasksWithoutBinaryData;
              set({ jobsTasks: { ...jobsTasks }, loading: false });
            }
          }
        }

        hideVeil();
      } else {
        // response was falsy: either a genuine failure (not ready / invalid),
        // or the backend already committed this submission on an earlier
        // attempt and its existing-tasks guard returned false, or the client
        // threw before it could read a successful response. Check server
        // truth before telling the technician it failed.
        const wasActuallySubmitted = await reconcileSubmittedTasks(sowKey);
        hideVeil();
        if (!wasActuallySubmitted && queryParamsService.getCacheKey() === sowKey) {
          alert(t('failedToSubmitTasks'));
        }
      }
    } catch (err) {
      console.error('Failed to submit: ' + err);
      const wasActuallySubmitted = await reconcileSubmittedTasks(sowKey);
      hideVeil();
      if (!wasActuallySubmitted && queryParamsService.getCacheKey() === sowKey) {
        alert(t('failedToSubmitTasks'));
      }
    }
  },
  
  togglePauseTask: async (taskId) => {
    const { jobsTasks } = get();
    let sowKey = queryParamsService.getCacheKey();
    let tasks = jobsTasks[sowKey] || [];
    const now = Date.now();
    
    const updatedTasks = tasks.map(task => {
      if (task.id === taskId && !isPartsUsageTask(task)) {
        if (!task.paused) {
          // Pausing the task - record pause start time
          return {
            ...task,
            paused: true,
            pauseStartTime: now,
            lastModified: Date.now()
          };
        } else {
          // Resuming the task - calculate paused duration and update total
          const pauseDuration = Math.floor((now - (task.pauseStartTime || now)) / 1000);
          const updatedTotalPausedTime = (task.totalPausedTime || 0) + pauseDuration;
          
          return {
            ...task,
            paused: false,
            lastResumeTime: now,
            totalPausedTime: updatedTotalPausedTime,
            pauseStartTime: null,
            lastModified: Date.now()
          };
        }
      }
      return task;
    });
    
    jobsTasks[sowKey] = updatedTasks;
    set({ jobsTasks: { ...jobsTasks } });
  },
  
  toggleTaskCompletion: async (taskId, t) => {
    const { jobsTasks, isTaskReadyToStop, fetchLocation } = get();
    let sowKey = queryParamsService.getCacheKey();
    let tasks = jobsTasks[sowKey] || [];
    const { alert } = useDialogStore.getState();
    
    const updatedTasks = tasks.map(task => {
      if (task.id === taskId && !isPartsUsageTask(task)) {
        task = get().updateTimeSpent(task);
        // Reopening a completed task must stay possible even when its time is empty.
        if (!task.completed && !hasTotalTime(task)) {
          alert(task.startTime ? t('taskTotalTimeEmpty', { id: task.id }) : t('taskNotStarted'));
          return task;
        }

        if (!task.completed) {
          if (!isTaskReadyToStop(taskId, t)) {
            return task;
          }

          if (!task.location) {
            const taskKey = task.key;
            setTimeout(() => {
              // The task may have been renumbered by the backfill during the delay
              const current = (get().jobsTasks[queryParamsService.getCacheKey()] || []).find((x) => isSameTask(x, taskId, taskKey));
              if (current) fetchLocation(current.id, t);
            }, 500);
          }
          if (!task.startTime) {
            if (task.timeSpent) {
              const date = new Date();
              date.setTime(date.getTime() - (task.timeSpent * 1000));
              task.startTime = date.toISOString();
            } else {
              task.startTime = new Date().toISOString();
            }
          }
        }
        
        const status = task.timeSpent > 0 ? CONSTANTS.STATUS.COMPLETED : CONSTANTS.STATUS.NOT_STARTED;
        
        // If we're undoing completion (task.completed is true), reset all timer fields
        if (task.completed) {
          return { 
            ...task,
            completed: false,
            status: CONSTANTS.STATUS.NOT_STARTED,
            paused: false,
            location: null,
            startTime: null,
            endTime: null,
            timeSpent: null,
            // Reset all timer tracking fields
            startTimestamp: undefined,
            lastResumeTime: undefined,
            totalPausedTime: undefined,
            pauseStartTime: null,
            lastModified: Date.now()
          };
        } else {
          // Marking as completed
          return { 
            ...task,
            completed: true,
            status: status,
            paused: false,
            endTime: new Date().toISOString(),
            lastModified: Date.now()
          };
        }
      }
      return task;
    });
    
    jobsTasks[sowKey] = updatedTasks;
    set({ jobsTasks: { ...jobsTasks } });
  },
  
  updateTaskTime: async (taskId, newTime) => {
    const { jobsTasks } = get();
    let sowKey = queryParamsService.getCacheKey();
    let tasks = jobsTasks[sowKey] || [];
    const currentTime = new Date();
    
    const updatedTasks = tasks.map(task => {
      if (task.id === taskId && !isPartsUsageTask(task)) {
        // Calculate the new timeSpent
        const currentTimeSpent = task.timeSpent || 0;
        const updatedTimeSpent = Math.max(0, currentTimeSpent + newTime);
        
        const adjustmentMilliseconds = newTime * 1000; // Convert seconds to milliseconds
        
        let newStartTime;
        
        if (task.startTime) {
          const currentStartTime = new Date(task.startTime);
          const proposedStartTime = new Date(currentStartTime.getTime() - adjustmentMilliseconds);
          newStartTime = proposedStartTime > currentTime ? currentTime : proposedStartTime;
        } else {
          const proposedStartTime = new Date(currentTime.getTime() - (updatedTimeSpent * 1000));
          newStartTime = proposedStartTime > currentTime ? currentTime : proposedStartTime;
        }

        /*if (task.status !== CONSTANTS.STATUS.COMPLETED && !task.paused && task.startTimestamp) {

          return { 
            ...task,
            startTime: newStartTime.toISOString(),
            startTimestamp: task.startTimestamp ? 
              Math.min(task.startTimestamp - adjustmentMilliseconds, currentTime.getTime()) : 
              undefined
          };
        } else {*/
        let finalTimeSpent = currentTimeSpent + newTime
        return { 
          ...task, 
          timeSpent: Math.max(0, finalTimeSpent),
          startTime: newStartTime.toISOString(),
          startTimestamp: task.startTimestamp ? 
            Math.min(task.startTimestamp - adjustmentMilliseconds, currentTime.getTime()) : 
            undefined,
          lastModified: Date.now()
        };
        //}

      }
      return task;
    });
    
    jobsTasks[sowKey] = updatedTasks;
    set({ jobsTasks: { ...jobsTasks } });
  },
  
  // Manually set a task's start and/or completed date/time (ISO strings).
  // Returns true when applied, false when rejected (an alert explains why).
  updateTaskDates: (taskId, { startTime, endTime }, t) => {
    const { jobsTasks } = get();
    const sowKey = queryParamsService.getCacheKey();
    const tasks = jobsTasks[sowKey] || [];
    const task = tasks.find(item => item.id === taskId);
    if (!task || !task.startTime) return false;

    const { alert } = useDialogStore.getState();
    const now = Date.now();
    const newStart = new Date(startTime || task.startTime).getTime();
    const newEnd = task.completed ? new Date(endTime || task.endTime).getTime() : null;

    if (isNaN(newStart) || (task.completed && isNaN(newEnd))) {
      alert(t('invalidDate'));
      return false;
    }
    if (newStart > now || (task.completed && newEnd > now)) {
      alert(t('dateInFuture'));
      return false;
    }
    if (task.completed && !isEndAfterStartByMinute(newStart, newEnd)) {
      alert(t(newEnd < newStart ? 'endBeforeStart' : 'endSameAsStart'));
      return false;
    }

    const updatedTasks = tasks.map(item => {
      if (item.id !== taskId) return item;
      const updated = { ...item, startTime: new Date(newStart).toISOString(), lastModified: now };
      if (item.completed) {
        updated.endTime = new Date(newEnd).toISOString();
        updated.timeSpent = Math.max(0, Math.floor((newEnd - newStart) / 1000) - (item.totalPausedTime || 0));
        return updated;
      }
      // Running/paused: keep the timer consistent with the new start.
      if (item.startTimestamp) {
        updated.startTimestamp = newStart;
        return get().updateTimeSpent(updated);
      }
      return updated;
    });

    set({ jobsTasks: { ...jobsTasks, [sowKey]: updatedTasks } });
    return true;
  },

  fetchLocation: async (taskId, t) => {
    const { jobsTasks } = get();
    let sowKey = queryParamsService.getCacheKey();
    let tasks = jobsTasks[sowKey] || [];
    const taskKey = findTaskIdentity(tasks, taskId);

    // Reset location state
    const resetTasks = tasks.map(task =>
      isSameTask(task, taskId, taskKey)
        ? { ...task, location: null, locationError: null, lastModified: Date.now() }
        : task
    );
    
    jobsTasks[sowKey] = resetTasks;
    set({ jobsTasks: { ...jobsTasks } });
    
    try {
      const location = await getCurrentLocation(t);

      // The technician switched tickets while waiting for the GPS fix; this ticket's tasks
      // are no longer in memory and must not be recreated from nothing.
      if (!get().jobsTasks[sowKey]) return location;
      
      // Get fresh tasks state after reset
      const currentJobsTasks = get().jobsTasks;
      let currentTasks = currentJobsTasks[sowKey] || [];
      
      const updatedTasks = currentTasks.map(task =>
        isSameTask(task, taskId, taskKey)
          ? { ...task, location, lastModified: Date.now() }
          : task
      );
      
      currentJobsTasks[sowKey] = updatedTasks;
      set({ jobsTasks: { ...currentJobsTasks } });
      return location;
    } catch (error) {
      if (!get().jobsTasks[sowKey]) throw error;
      // Get fresh tasks state after reset
      const currentJobsTasks = get().jobsTasks;
      let currentTasks = currentJobsTasks[sowKey] || [];
      
      const updatedTasks = currentTasks.map(task =>
        isSameTask(task, taskId, taskKey)
          ? { ...task, locationError: error.message, lastModified: Date.now() }
          : task
      );
      
      currentJobsTasks[sowKey] = updatedTasks;
      set({ jobsTasks: { ...currentJobsTasks } });
      throw error;
    }
  },

  filteredAndSortedTasks: () => {
    const { jobsTasks, searchTerm, sortField, sortDirection } = get();
    let sowKey = queryParamsService.getCacheKey();
    let tasks = jobsTasks[sowKey] || [];
    
    return tasks
      .filter(task => 
        task.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];
        
        if (sortField === 'description') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
  },

  statistics: () => {
    const { jobsTasks } = get();
    let sowKey = queryParamsService.getCacheKey();
    let tasks = jobsTasks[sowKey] || [];
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(task => task.status === CONSTANTS.STATUS.COMPLETED).length;
    const inProgressTasks = tasks.filter(task => task.status === CONSTANTS.STATUS.IN_PROGRESS).length;
    const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    const totalTimeSpent = tasks.reduce((total, task) => total + task.timeSpent, 0);
    
    return {
      totalTasks,
      completedTasks,
      inProgressTasks,
      completionPercentage,
      totalTimeSpent
    };
  }
}), {
  // Partitioned per ticket (issueKey+token from the URL) so tabs working on
  // different tickets can't overwrite each other's unsynced entries — see
  // getUrlCacheKey() and createTicketTasksStorage().
  name: `${TICKET_TASKS_STORAGE_PREFIX}${getUrlCacheKey()}`,
  storage: createTicketTasksStorage(getUrlCacheKey, ticketFollower.isLoaded),
  onRehydrateStorage: () => ticketFollower.onHydrated
}));